import { isCover, trimmedPageBox, type PageBox, type ZonePrintSizes } from './zone-shapes';

/**
 * Допуск в книжных px. Касание считается вылетом, поэтому сравнение строгое с запасом: край, севший
 * ровно на линию реза, читается как «на ней», а не как «внутри».
 */
const TRIM_TOUCH_EPSILON = 0.01;

/**
 * Развороту, на котором судится альбомный дефолт: печатная страница плюс признак обложки. Ровно эта
 * пара едет и в Rust-конверт (`trim` + отсутствующий `frameStroke`), поэтому редактор, плитки и печать
 * судят по одному и тому же.
 */
export interface AlbumStrokeScope {
  /** Печатная страница в книжных px — лист минус вылет. */
  trim: PageBox;
  /** Обложка обводки не получает вовсе; прямоугольником реза этот запрет не выразить. */
  cover: boolean;
}

/** Печатные размеры разворота → область, в которой альбомный дефолт вообще возможен. */
export function albumStrokeScopeOf(printSizes: ZonePrintSizes): AlbumStrokeScope {
  return { trim: trimmedPageBox(printSizes), cover: isCover(printSizes) };
}

/**
 * Может ли рамка взять АЛЬБОМНУЮ обводку. Правило про дефолт и только про него: своя обводка рамки
 * рисуется всегда — старые альбомы остаются как есть.
 *
 * Нельзя двум: обложке (обводки не получает вовсе) и рамке, которая пересекает линию реза или лежит
 * на ней — такой кадр уходит в вылет, и линия по его краю обрезалась бы вместе с ним.
 *
 * `rect` — АБСОЛЮТНАЯ геометрия бокса на листе, в тех же книжных px, что и печатные размеры.
 */
export function frameStrokeAllowedIn(rect: PageBox, scope: AlbumStrokeScope): boolean {
  if (scope.cover) return false;
  const trim = scope.trim;
  return (
    rect.x > trim.x + TRIM_TOUCH_EPSILON &&
    rect.y > trim.y + TRIM_TOUCH_EPSILON &&
    rect.x + rect.width < trim.x + trim.width - TRIM_TOUCH_EPSILON &&
    rect.y + rect.height < trim.y + trim.height - TRIM_TOUCH_EPSILON
  );
}

/** Тот же вопрос, когда у вызывающего на руках печатные размеры, а не готовая область. */
export function frameStrokeAllowed(rect: PageBox, printSizes: ZonePrintSizes): boolean {
  return frameStrokeAllowedIn(rect, albumStrokeScopeOf(printSizes));
}
